import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  UserPlus, Phone, MessageSquare, AlertTriangle, Calendar,
  GraduationCap, Star, CheckCircle, XCircle, Clock, ChevronDown,
  ChevronUp, PhoneCall, PhoneOff, PhoneMissed, Send, Shield,
} from 'lucide-react';

const EVENT_CONFIG = {
  lead_captured: {
    icon: UserPlus,
    color: 'bg-pink-100 text-pink-600',
    dotColor: 'bg-pink-500',
    label: 'Lead Captured',
  },
  ai_call_attempt: {
    icon: Phone,
    color: 'bg-blue-100 text-blue-600',
    dotColor: 'bg-blue-500',
    label: 'AI Call',
  },
  ai_sms_sent: {
    icon: MessageSquare,
    color: 'bg-indigo-100 text-indigo-600',
    dotColor: 'bg-indigo-500',
    label: 'AI SMS Sent',
  },
  escalated: {
    icon: AlertTriangle,
    color: 'bg-amber-100 text-amber-700',
    dotColor: 'bg-amber-500',
    label: 'Escalated to Staff',
  },
  callback_scheduled: {
    icon: PhoneCall,
    color: 'bg-violet-100 text-violet-600',
    dotColor: 'bg-violet-500',
    label: 'Callback Scheduled',
  },
  callback_completed: {
    icon: PhoneCall,
    color: 'bg-green-100 text-green-600',
    dotColor: 'bg-green-500',
    label: 'Callback Completed',
  },
  callback_no_answer: {
    icon: PhoneMissed,
    color: 'bg-orange-100 text-orange-600',
    dotColor: 'bg-orange-400',
    label: 'Callback — No Answer',
  },
  registration_booked: {
    icon: Calendar,
    color: 'bg-green-100 text-green-600',
    dotColor: 'bg-green-500',
    label: 'Registration Booked',
  },
  registration_attended: {
    icon: CheckCircle,
    color: 'bg-green-100 text-green-700',
    dotColor: 'bg-green-600',
    label: 'Registration Attended',
  },
  course_enrolled: {
    icon: GraduationCap,
    color: 'bg-pink-100 text-pink-600',
    dotColor: 'bg-pink-500',
    label: 'Enrolled in Course',
  },
  course_completed: {
    icon: GraduationCap,
    color: 'bg-emerald-100 text-emerald-700',
    dotColor: 'bg-emerald-600',
    label: 'Course Completed',
  },
  review_requested: {
    icon: Star,
    color: 'bg-amber-100 text-amber-600',
    dotColor: 'bg-amber-500',
    label: 'Review Requested',
  },
  review_received: {
    icon: Star,
    color: 'bg-yellow-100 text-yellow-700',
    dotColor: 'bg-yellow-500',
    label: 'Review Received',
  },
  lead_not_eligible: {
    icon: Shield,
    color: 'bg-red-100 text-red-600',
    dotColor: 'bg-red-400',
    label: 'Not Eligible',
  },
  lead_lost: {
    icon: XCircle,
    color: 'bg-slate-100 text-slate-500',
    dotColor: 'bg-slate-400',
    label: 'Lost',
  },
  lead_booked: {
    icon: Calendar,
    color: 'bg-green-100 text-green-600',
    dotColor: 'bg-green-500',
    label: 'Booked',
  },
  note_added: {
    icon: MessageSquare,
    color: 'bg-slate-100 text-slate-600',
    dotColor: 'bg-slate-400',
    label: 'Note Added',
  },
  message_received: {
    icon: MessageSquare,
    color: 'bg-cyan-100 text-cyan-600',
    dotColor: 'bg-cyan-500',
    label: 'Message Received',
  },
  message_sent: {
    icon: Send,
    color: 'bg-sky-100 text-sky-600',
    dotColor: 'bg-sky-500',
    label: 'Message Sent',
  },
};

const DEFAULT_CONFIG = {
  icon: Clock,
  color: 'bg-slate-100 text-slate-500',
  dotColor: 'bg-slate-400',
  label: 'Event',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;

  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getCallOutcomeLabel(outcome) {
  const map = {
    answered: 'Answered',
    no_answer: 'No Answer',
    voicemail: 'Voicemail',
    busy: 'Busy',
    failed: 'Failed',
    completed: 'Completed',
    interested: 'Interested',
    not_interested: 'Not Interested',
  };
  return map[outcome] || outcome;
}

function getCallOutcomeColor(outcome) {
  if (['answered', 'completed', 'interested'].includes(outcome)) return 'bg-green-100 text-green-700';
  if (['no_answer', 'voicemail', 'busy'].includes(outcome)) return 'bg-amber-100 text-amber-700';
  if (['failed', 'not_interested'].includes(outcome)) return 'bg-red-100 text-red-600';
  return 'bg-slate-100 text-slate-600';
}

const TimelineEvent = ({ event, isLast, onScheduleCallback }) => {
  const [expanded, setExpanded] = useState(false);
  const config = EVENT_CONFIG[event.type] || DEFAULT_CONFIG;
  const Icon = config.icon;
  const payload = event.event_payload || event.payload || {};

  const hasExpandableContent =
    event.type === 'ai_call_attempt' ||
    event.type === 'ai_sms_sent' ||
    event.type === 'escalated' ||
    event.type === 'callback_completed' ||
    event.type === 'callback_no_answer' ||
    payload.details ||
    payload.notes;

  return (
    <div className="flex gap-3 group">
      {/* Timeline rail */}
      <div className="flex flex-col items-center">
        <div className={`w-3 h-3 rounded-full ${config.dotColor} ring-4 ring-white flex-shrink-0 mt-1.5`} />
        {!isLast && <div className="w-0.5 bg-slate-200 flex-1 min-h-[24px]" />}
      </div>

      {/* Content */}
      <div className={`flex-1 pb-4 ${isLast ? '' : ''}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`text-xs font-medium px-2 py-0.5 ${config.color}`}>
              <Icon className="w-3 h-3 mr-1" />
              {config.label}
            </Badge>

            {/* Call attempt number */}
            {event.type === 'ai_call_attempt' && payload.call_number && (
              <span className="text-xs text-slate-400">Call #{payload.call_number}</span>
            )}

            {/* SMS number */}
            {event.type === 'ai_sms_sent' && payload.sms_number && (
              <span className="text-xs text-slate-400">SMS #{payload.sms_number}</span>
            )}

            {/* Call outcome badge */}
            {event.type === 'ai_call_attempt' && payload.outcome && (
              <Badge className={`text-xs ${getCallOutcomeColor(payload.outcome)}`}>
                {getCallOutcomeLabel(payload.outcome)}
              </Badge>
            )}

            {/* Callback outcome */}
            {(event.type === 'callback_completed' || event.type === 'callback_no_answer') && payload.outcome && (
              <Badge className={`text-xs ${getCallOutcomeColor(payload.outcome)}`}>
                {getCallOutcomeLabel(payload.outcome)}
              </Badge>
            )}

            {/* Escalation reason */}
            {event.type === 'escalated' && payload.reason && (
              <span className="text-xs text-amber-600 font-medium">
                {payload.reason.replace(/_/g, ' ')}
              </span>
            )}

            {/* Course name for enrollment */}
            {event.type === 'course_enrolled' && payload.course_name && (
              <span className="text-xs text-slate-500">{payload.course_name}</span>
            )}

            {/* Registration date */}
            {event.type === 'registration_booked' && payload.registration_date && (
              <span className="text-xs text-slate-500">
                for {new Date(payload.registration_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </span>
            )}
          </div>

          <span className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0">
            {formatDate(event.date || event.created_at)}
          </span>
        </div>

        {/* Call duration */}
        {event.type === 'ai_call_attempt' && payload.duration > 0 && (
          <p className="text-xs text-slate-400 mt-1">
            Duration: {Math.floor(payload.duration / 60)}m {payload.duration % 60}s
          </p>
        )}

        {/* SMS preview */}
        {event.type === 'ai_sms_sent' && payload.content_preview && (
          <p className="text-xs text-slate-500 mt-1 italic">"{payload.content_preview}"</p>
        )}

        {/* Expandable section */}
        {hasExpandableContent && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 mt-1 transition-colors"
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? 'Less' : 'More details'}
          </button>
        )}

        {expanded && (
          <div className="mt-2 p-3 bg-slate-50 rounded-xl text-xs text-slate-600 space-y-1.5">
            {event.type === 'ai_call_attempt' && (
              <>
                {payload.transcript_url && (
                  <a
                    href={payload.transcript_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pink-600 hover:underline block"
                  >
                    View transcript / recording
                  </a>
                )}
                {payload.outcome && <p>Outcome: {getCallOutcomeLabel(payload.outcome)}</p>}
                {payload.duration > 0 && (
                  <p>Duration: {Math.floor(payload.duration / 60)}m {payload.duration % 60}s</p>
                )}
              </>
            )}
            {event.type === 'ai_sms_sent' && payload.content_preview && (
              <p>Message: "{payload.content_preview}"</p>
            )}
            {event.type === 'escalated' && (
              <p>Reason: {(payload.reason || 'AI could not reach student').replace(/_/g, ' ')}</p>
            )}
            {(payload.details || payload.notes) && (
              <p>{payload.details || payload.notes}</p>
            )}

            {/* Quick actions in expanded view */}
            {(event.type === 'escalated' || event.type === 'callback_no_answer') && onScheduleCallback && (
              <Button
                size="sm"
                variant="outline"
                className="mt-2 text-xs h-7 rounded-lg border-pink-200 text-pink-600 hover:bg-pink-50"
                onClick={(e) => {
                  e.stopPropagation();
                  onScheduleCallback();
                }}
              >
                <PhoneCall className="w-3 h-3 mr-1" />
                Schedule Callback
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CustomerJourney = ({ timeline = [], onScheduleCallback }) => {
  if (!timeline.length) {
    return (
      <div className="py-8 text-center">
        <Clock className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-400">No activity yet</p>
      </div>
    );
  }

  // Sort by date descending (newest first)
  const sorted = [...timeline].sort(
    (a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at)
  );

  return (
    <div className="py-2">
      {sorted.map((event, idx) => (
        <TimelineEvent
          key={event.event_id || `${event.type}-${idx}`}
          event={event}
          isLast={idx === sorted.length - 1}
          onScheduleCallback={onScheduleCallback}
        />
      ))}
    </div>
  );
};

export default CustomerJourney;
